import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Honeybadger from '@honeybadger-io/js';
import { useFormikContext } from 'formik';

import { requestApi } from 'api/Api';
import { getStates as getStatesAPI } from 'api/State';

import { optionify } from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Input from 'components/shared/Input/Input';
import Select from 'components/shared/Select/Select';

const AccountAddress = forwardRef(({ practice }, ref) => {
  const [states, setStates] = useState([]);

  const navigate = useNavigate();
  const { setFieldValue, values, errors } = useFormikContext();

  const onAddressChange = (event) => {
    let field;
    let value;
    if (event?.name == 'state') {
      field = event?.name;
      value = event?.value || null;
    } else {
      field = event.target?.name;
      value = event.target?.value;
    }
    setFieldValue('address', {
      ...values?.address,
      [field]: value,
      full_street_address: `${values?.address?.address_ln_1}, ${values?.address?.address_ln_2}, ${values?.address?.city}, ${values?.address?.state}, ${values?.address.country}, ${values?.address.zip}`
    });
  };

  const findStateName = (value) => {
    const state = states?.find((state) => {
      if (state?.id == value?.state) {
        return state;
      }
    });

    return state && state?.name;
  };

  useImperativeHandle(ref, () => ({
    updateAddress
  }));

  const updateAddress = async () => {
    try {
      if (values?.address) {
        try {
          const res = await requestApi({
            url: '/api/address/patch',
            params: {
              loadedAddress: values?.address
            },
            navigate
          });
          if (res.code === 0) {
            setFieldValue('address', values?.address);
          } else {
            showAlert({
              message: 'An error has occurred. Please try again later.', // We only trigger error on api fail
              color: 'danger'
            });
            Honeybadger.notify(
              `practice: ${practice.id}, file: /practice/settings/account/index.js, method: updateAddress, error`
            );
          }
        } catch (error) {
          Honeybadger.notify(
            `practice: ${practice.id}, file: /practice/settings/account/index.js, method: updateAddress, error: ${error}`
          );
        }
      }
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: /practice/settings/account/index.js, method: updateAddress, error: ${error}`
      );
    }
  };

  const getStates = async () => {
    try {
      const res = await getStatesAPI(navigate);
      setStates(res);
    } catch (error) {
      Honeybadger.notify(
        `practice: ${practice.id}, file: /practice/settings/account/index.js, method: getStates, error:${error}`
      );
    }
  };
  useEffect(() => {
    getStates();
  }, []);

  return (
    states && (
      <div className="flex w-full flex-col justify-between gap-5 border-b border-l-0 border-r-0 border-t-0 border-solid border-b-neutral-100 py-5">
        <div className="flex flex-col gap-5">
          <span className="text-sm font-500">Practice Address</span>
        </div>
        <div className="grid w-full grid-cols-2 gap-5">
          <div>
            <Input
              label="Address"
              type="text"
              placeholder="Address"
              name="address_ln_1"
              value={values?.address?.address_ln_1}
              onChange={(e) => onAddressChange(e)}
              error={errors?.address?.address_ln_1}
            />
          </div>
          <div>
            <Input
              label="Address 2"
              type="text"
              placeholder="Address 2"
              name="address_ln_2"
              value={values?.address?.address_ln_2}
              onChange={(e) => onAddressChange(e)}
            />
          </div>
          <div>
            <Input
              label="City"
              type="text"
              placeholder="City"
              name="city"
              value={values?.address?.city}
              onChange={(e) => onAddressChange(e)}
              error={errors?.address?.city}
            />
          </div>
          <div>
            <Select
              label="State"
              inputId="state-select"
              placeholder="Type and select"
              name="state"
              value={{ label: findStateName(values?.address) }}
              onChange={(e) => onAddressChange({ ...e, name: 'state' })}
              options={optionify(states, 'name', 'id')}
              error={errors?.address?.state}
            />
          </div>
          <div>
            <Input
              label="Zip code"
              type="text"
              placeholder="Zip"
              name="zip"
              value={values?.address?.zip}
              onChange={(e) => onAddressChange(e)}
              error={errors?.address?.zip}
            />
          </div>
        </div>
      </div>
    )
  );
});

export default withErrorBoundary(AccountAddress);
